/**
 * Next-case navigation for the surfacer (report review) viewer.
 *
 * Resolves the worklist the reader came from — `?collection=` (all of its
 * cohorts, filtered to its studies) or else the surfacer response's own cohort —
 * and picks the next outstanding study via `nextUnreviewedSurfacerStudy`. Reads
 * the same cached queries the worklist uses (reader cohort, all decisions, all
 * skips), so opening the viewer from the worklist costs no extra requests.
 * Navigation keeps the collection param so the worklist context survives.
 *
 * With "Only unjudged" on (`useOnlyUnjudged`), it also loads each cohort's
 * pooled review state and skips studies nobody has anything left to judge on,
 * and reports how many unjudged findings the list still holds.
 */

import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCollections } from "./useCollections";
import { useAllSkippedStudies } from "./useSkippedStudies";
import { useReaderCohorts, type ReaderStudyItem } from "./useStudyQueries";
import { useAllSurfacerDecisions } from "./useSurfacerDecisions";
import { useOnlyUnjudged } from "./useOnlyUnjudged";
import { useSurfacerReviewStates } from "./useSurfacerReviewState";
import { readerCaseId, readerCaseViewerPath } from "../utils/readerCaseId";
import { nextUnreviewedSurfacerStudy, unjudgedTally } from "../utils/surfacerNextCase";

export interface SurfacerNextCase {
  /** The next outstanding study, or null when none is left (or still loading). */
  next: ReaderStudyItem | null;
  /** True until the worklist, decisions and skips (and, with "Only unjudged"
   * on, the review states) have all loaded. */
  isLoading: boolean;
  goToNext: () => void;
  /** "Only unjudged" is on: Next case skips studies with nothing left to judge. */
  onlyUnjudged: boolean;
  setOnlyUnjudged: (value: boolean) => void;
  /** Findings nobody has judged across this worklist, and the cases holding
   * them. Null while "Only unjudged" is off or still loading. */
  unjudgedLeft: { findings: number; cases: number } | null;
}

export function useSurfacerNextCase(cohortId: string, studyUid: string): SurfacerNextCase {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const collectionParam = searchParams.get("collection");
  const { collections } = useCollections();
  const collection = collectionParam ? collections.get(collectionParam) : undefined;

  const cohortIds = useMemo(
    () => (collection ? collection.cohortIds : [cohortId]),
    [collection, cohortId],
  );
  const { data: worklist } = useReaderCohorts(token, cohortIds);
  const { data: decisions } = useAllSurfacerDecisions(!!token);
  const { data: skipped } = useAllSkippedStudies(!!token);
  const { onlyUnjudged, setOnlyUnjudged } = useOnlyUnjudged();
  const reviewStates = useSurfacerReviewStates(cohortIds, onlyUnjudged && !!token);

  const studies = useMemo(() => {
    if (!worklist) return null;
    return collection
      ? worklist.studies.filter((s) => collection.studyUids.has(s.study_uid))
      : worklist.studies;
  }, [worklist, collection]);

  const isLoading = !studies || !decisions || !skipped || reviewStates.isLoading;

  const next = useMemo(() => {
    if (!studies || !decisions || !skipped || reviewStates.isLoading) return null;
    return nextUnreviewedSurfacerStudy(
      studies,
      studyUid,
      cohortId,
      decisions,
      skipped,
      onlyUnjudged ? reviewStates.byCohort : null,
    );
  }, [studies, decisions, skipped, studyUid, cohortId, onlyUnjudged, reviewStates]);

  const unjudgedLeft = useMemo(() => {
    if (!onlyUnjudged || !studies || !decisions || !skipped || reviewStates.isLoading) return null;
    return unjudgedTally(studies, cohortId, reviewStates.byCohort, decisions, skipped);
  }, [onlyUnjudged, studies, cohortId, reviewStates, decisions, skipped]);

  return {
    next,
    isLoading,
    onlyUnjudged,
    setOnlyUnjudged,
    unjudgedLeft,
    goToNext: () => {
      if (!next) return;
      navigate(
        readerCaseViewerPath(readerCaseId(next), next.cohort_id ?? cohortId, collectionParam),
      );
    },
  };
}
